import { Avatar } from '@dev-spendesk/grapes';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import {
  useTranslation,
  type TGlobalFunctionTyped,
} from 'src/core/common/hooks/useTranslation';
import { models as approvalModels } from 'src/core/modules/company/structure/approval-flows';
import { useMembersQuery } from 'src/core/modules/members/hooks/useMembersQuery';
import { type Member } from 'src/core/modules/members/models/member';
import { formatMoney } from 'src/core/utils/money';

import { type ApprovalSchemeWithMemberNames } from '../../hooks/useApprovalSchemesQuery';

type Props = {
  approvalScheme: ApprovalSchemeWithMemberNames;
};

type ApprovalRuleWithMemberNames =
  ApprovalSchemeWithMemberNames['rules'][number];

type ApprovalRightWithMemberName =
  ApprovalRuleWithMemberNames['steps'][number]['rights'][number];

const renderRuleHeader = (
  rule: ApprovalRuleWithMemberNames,
  ruleIndex: number,
  isLastRule: boolean,
  t: TGlobalFunctionTyped,
) => {
  const formattedFromAmount = formatMoney(
    rule.from.value || 0,
    rule.from.currency,
  );
  const formattedUpToAmount = formatMoney(
    rule.upTo.value || 0,
    rule.upTo.currency,
  );

  // Single rule case: can approve all requests
  if (isLastRule && ruleIndex === 0) {
    return (
      <span className="text-content-primary title-m">
        {t('teams.approvalFlows.canApproveAllRequests')}
      </span>
    );
  }

  // Last rule in multi-rule: "> X"
  if (isLastRule) {
    return (
      <div className="flex items-center gap-4">
        <span className="title-m text-content-brand-default">&gt;</span>
        <span className="title-m">{formattedFromAmount}</span>
      </div>
    );
  }

  // First rule in multi-rule: "< X"
  if (rule.from.value === 0 || rule.from.value === null) {
    return (
      <div className="flex items-center gap-4">
        <span className="title-m text-content-brand-default">&lt;</span>
        <span className="title-m">{formattedUpToAmount}</span>
      </div>
    );
  }

  // Middle rule: "X → Y"
  return (
    <div className="flex items-center gap-4">
      <span className="title-m">{formattedFromAmount}</span>
      <span className="title-m text-content-brand-default">→</span>
      <span className="title-m">{formattedUpToAmount}</span>
    </div>
  );
};

const getApproverDisplayName = (
  right: ApprovalRightWithMemberName,
  t: TGlobalFunctionTyped,
): string => {
  if (right.approverType === 'user') {
    return right.approverName || right.approverId;
  }
  if (right.approverType === 'accountOwner') {
    return t('teams.approvalFlows.accountOwner');
  }
  if (right.approverType === 'reportingManager') {
    return t('teams.approvalFlows.reportingManager');
  }
  if (right.approverType === 'costCenterOwner') {
    return t('teams.approvalFlows.costCenterOwner');
  }
  return '';
};

export const ApprovalSchemePreview = ({ approvalScheme }: Props) => {
  const { t } = useTranslation('global');
  const membersQuery = useMembersQuery();
  const members = unwrapQuery(membersQuery);
  const rules = approvalScheme.rules ?? [];

  if (rules.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-16 text-left">
      <div className="body-m text-content-secondary-bg-primary self-baseline">
        {t('approvalWorkflows.approvalScheme')}
      </div>
      {rules.map((rule, ruleIndex) => {
        const isMultiStep = approvalModels.isMultiStep(rule.steps);
        const isLastRule = approvalModels.isTheLastRule(rules, ruleIndex);
        const approvers = isMultiStep
          ? rule.steps.flatMap((step) => step.rights)
          : rule.steps
              .flatMap((step) => step.rights)
              .sort((a, b) => {
                return a.approverName?.localeCompare(b.approverName ?? '') ?? 0;
              });

        return (
          <div
            className="rounded-8 bg-background-secondary-default flex flex-col p-8"
            key={rule.id}
          >
            <div className="mb-8 text-left">
              {renderRuleHeader(rule, ruleIndex, isLastRule, t)}
            </div>
            <div className="relative flex flex-col">
              {approvers.map((approver, approverIndex) => {
                const displayName = getApproverDisplayName(approver, t);
                const key =
                  approver.approverType === 'user'
                    ? approver.approverId
                    : `${approver.approverType}-${approverIndex}`;

                return (
                  <div className="flex items-center gap-8 py-8" key={key}>
                    {isMultiStep && (
                      <span className="rounded-4 bg-background-secondary-info-default body-s text-content-info-default z-10 flex h-20 w-20 shrink-0 items-center justify-center font-semibold">
                        {approverIndex + 1}
                      </span>
                    )}
                    <Avatar
                      size={24}
                      src={
                        approver.approverType === 'user'
                          ? getMemberAvatar(approver.approverId, members ?? [])
                          : undefined
                      }
                      text={displayName}
                    />
                    <span className="body-m text-content-primary">
                      {displayName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getMemberAvatar = (
  memberId: string,
  members: Member[],
): string | undefined => {
  const member = members.find((m) => m.id === memberId);
  return member?.avatar ?? undefined;
};
